import { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import classNames from 'clsx';
import { playbackStatusIcons } from 'src/common/utils/playbackStatusIconMap';
import ClampText from '../../../shared/ClampText';
import GuideItemTitle from '../../shared/GuideItemTitle';
import PlayButton from '../../shared/PlayButton';
import ImageWithDefault from '../../../shared/ImageWithDefault';
import isSelectable from '../../../../utils/guideItem/isSelectable';
import css from '../../shared/guide-item.module.scss';
import commonCss from '../../../../styles/common.module.scss';
import tileCss from './tile.module.scss';

const Tile = ({ guideItem, index, getStyles, onClick, isMatrix }) => {
  const [isSelected, setIsSelected] = useState(false);
  const isSelectableItem = isSelectable(guideItem);
  const playbackStatusIcon = get(
    guideItem,
    'properties.playbackStatus.statusIcon',
  );
  const isNewPlaybackItem =
    playbackStatusIcon === playbackStatusIcons.newPlaybackItem;
  const { tileSize, tileTitle, tileShape, tileTitleAlignment } = getStyles(
    guideItem.style,
  );

  const squareTileOverlayClassNames = classNames({
    [tileCss.selectableOverlay]: isSelectableItem,
    [commonCss.overlay]: !isSelectableItem,
    [tileCss.matrixOverlay]: isMatrix,
  });

  const dotClassNames = classNames(css.dot, {
    [css[`dot${tileSize}`]]: tileSize,
  });

  const dogEarClassNames = classNames(css.dogEar, {
    [css[`dogEar${tileSize}`]]: tileSize,
  });

  const captionContainerClassNames = classNames(tileCss.captionContainer, {
    [tileCss[`textAlign${tileTitleAlignment}`]]: tileTitleAlignment,
  });

  const clampTextClassNames = classNames(css.guideItemTitleMultiLine, {
    [tileCss.selectableGuideItemTitle]: isSelectableItem,
  });

  function handleClick() {
    onClick(guideItem.guideId);

    if (isSelectableItem) {
      setIsSelected((_isSelected) => !_isSelected);
    }
  }

  return (
    <div
      data-testid="squareTileOverlay"
      className={squareTileOverlayClassNames}
      onClick={handleClick}
    >
      <div
        className={tileCss.tileContainer}
        data-testid={`guideItem-${index}`}
        data-testtype="squareTile"
      >
        <PlayButton
          guideItem={guideItem}
          tileSize={tileSize}
          tileShape={tileShape}
          isSelected={isSelected}
        >
          <div
            className={tileCss.imageContainer}
            data-testid="guideItemImageWrapper"
          >
            {isNewPlaybackItem && (
              <div
                data-testid="newPlayableItemContainer"
                className={css.newPlayableItemContainer}
              >
                <div
                  data-testid="newPlayableItemIndicator"
                  className={dotClassNames}
                />
                <div className={dogEarClassNames} />
              </div>
            )}
            <ImageWithDefault
              dataTestId="guideItemImage"
              src={guideItem.image}
              alt={guideItem.title}
              className={tileCss.image}
            />
          </div>
        </PlayButton>
        <GuideItemTitle
          dataTestId="guideItemTitleLink"
          guideItem={guideItem}
          hasOwnTitleWrapper
          hasImage
        >
          {guideItem.title && tileTitle !== false && (
            <div
              className={captionContainerClassNames}
              data-testid="guideItemTitle"
            >
              <ClampText
                clamp={2}
                className={clampTextClassNames}
                useNativeClamp={false}
              >
                {guideItem.title}
              </ClampText>
            </div>
          )}
        </GuideItemTitle>
      </div>
    </div>
  );
};

Tile.propTypes = {
  guideItem: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  getStyles: PropTypes.func,
  onClick: PropTypes.func,
  isMatrix: PropTypes.bool,
};

Tile.defaultProps = {
  getStyles: () => ({}),
  onClick: () => {},
};

export default Tile;
